import { Box, Icon } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';

import { MilestoneTab } from './MilestoneTab';
import { SkeletonBox } from './SkeletonBox';

const CONTROL_WIDTH = '48px';

// Margins moved onto the relative wrapper so the controls align with the strip edges.
const milestoneStripWrapperStyle: CSSProperties = {
  position: 'relative',
  margin: 'var(--size-spacing-sm) var(--size-spacing-lg) var(--size-spacing-lg)',
};

const milestoneScrollContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  borderBottom: 'var(--size-border-xs) solid var(--color-border-separator)',
};

// Full-height, edge-anchored scroll control. The entire strip is the click target so a
// near-miss scrolls instead of falling through to the tab behind it; the gradient doubles
// as the "more content" fade and masks the partially-hidden tab at the edge.
const controlButtonBaseStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  width: CONTROL_WIDTH,
  padding: 0,
  border: 'none',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
};

const leftControlButtonStyle: CSSProperties = {
  ...controlButtonBaseStyle,
  left: 0,
  justifyContent: 'flex-start',
  paddingLeft: '4px',
  background: 'linear-gradient(to right, var(--color-background-primary) 45%, transparent)',
};

const rightControlButtonStyle: CSSProperties = {
  ...controlButtonBaseStyle,
  right: 0,
  justifyContent: 'flex-end',
  paddingRight: '4px',
  background: 'linear-gradient(to left, var(--color-background-primary) 45%, transparent)',
};

// Visible chevron affordance; the real click target is the full-height control above.
const arrowDiscStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: 'var(--color-background-secondary)',
  boxShadow: 'var(--size-box-shadow-sm)',
};

export interface MilestoneTabsProps {
  milestoneSummary: { totalCount?: number; milestones?: any[] };
  currentMilestone: string | null;
  isLoading: boolean;
  /** Stable selection callback owned by the parent (mutates the URL). `undefined` clears the filter. */
  onSelectMilestone: (type?: string) => void;
}

export function MilestoneTabs({
  milestoneSummary,
  currentMilestone,
  isLoading,
  onSelectMilestone,
}: MilestoneTabsProps) {
  const milestones = useMemo(() => milestoneSummary?.milestones ?? [], [milestoneSummary?.milestones]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLElement | null>>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const registerTabRef = useCallback((index: number, element: HTMLElement | null) => {
    tabRefs.current[index] = element;
  }, []);

  // Arrow/Home/End move focus only (roving tabindex). Selection stays manual (Enter/Space in the tab).
  const handleTabNavigate = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, index: number) => {
      const count = milestones.length + 1;
      let nextIndex: number;
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (index + 1) % count;
          break;
        case 'ArrowLeft':
          nextIndex = (index - 1 + count) % count;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = count - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      tabRefs.current[nextIndex]?.focus();
    },
    [milestones.length],
  );

  const updateScrollAffordance = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element) {
      return;
    }
    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }, []);

  const scrollByViewport = useCallback((direction: 1 | -1) => {
    const element = scrollContainerRef.current;
    if (!element) {
      return;
    }
    element.scrollBy({ left: direction * element.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  const handleScrollLeft = useCallback(() => scrollByViewport(-1), [scrollByViewport]);
  const handleScrollRight = useCallback(() => scrollByViewport(1), [scrollByViewport]);

  // Recompute fade/arrow affordance on mount, scroll, resize, and whenever the tab content changes.
  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) {
      return;
    }
    updateScrollAffordance();
    element.addEventListener('scroll', updateScrollAffordance, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollAffordance);
    resizeObserver.observe(element);
    return () => {
      element.removeEventListener('scroll', updateScrollAffordance);
      resizeObserver.disconnect();
    };
  }, [updateScrollAffordance, isLoading, milestones]);

  const activeTabIndex = useMemo(() => {
    if (!currentMilestone) {
      return 0;
    }
    const found = milestones.findIndex((milestone: any) => milestone?.type === currentMilestone);
    return found >= 0 ? found + 1 : 0;
  }, [currentMilestone, milestones]);

  // Bring the active tab into horizontal view (deep links can start it off-screen).
  // Adjust scrollLeft only via bounding rects so we never trigger vertical page scroll.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const container = scrollContainerRef.current;
    const activeElement = tabRefs.current[activeTabIndex];
    if (!container || !activeElement) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const edgePadding = 16;
    if (activeRect.left < containerRect.left) {
      container.scrollLeft -= containerRect.left - activeRect.left + edgePadding;
    } else if (activeRect.right > containerRect.right) {
      container.scrollLeft += activeRect.right - containerRect.right + edgePadding;
    }
  }, [activeTabIndex, isLoading, milestones]);

  return (
    <div style={milestoneStripWrapperStyle}>
      <div
        ref={scrollContainerRef}
        role={isLoading ? undefined : 'tablist'}
        aria-label={isLoading ? undefined : 'Filter accounts by milestone'}
        aria-orientation={isLoading ? undefined : 'horizontal'}
        style={milestoneScrollContainerStyle}
      >
        {isLoading ? (
          <Box height="52px" alignItems="center" direction="row" childGap="xl">
            <SkeletonBox width="80px" height="14px" />
            <SkeletonBox width="170px" height="14px" />
            <SkeletonBox width="80px" height="14px" />
            <SkeletonBox width="100px" height="14px" />
          </Box>
        ) : (
          <>
            <MilestoneTab
              index={0}
              label="All"
              count={milestoneSummary.totalCount || 0}
              isActive={!currentMilestone}
              onSelect={onSelectMilestone}
              onNavigate={handleTabNavigate}
              registerRef={registerTabRef}
            />
            {milestones.map((milestone: any, index: number) => (
              <MilestoneTab
                key={milestone?.type}
                index={index + 1}
                type={milestone?.type}
                label={milestone?.name}
                count={milestone?.count || 0}
                isActive={currentMilestone === milestone?.type}
                onSelect={onSelectMilestone}
                onNavigate={handleTabNavigate}
                registerRef={registerTabRef}
              />
            ))}
          </>
        )}
      </div>
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll milestones left"
          onClick={handleScrollLeft}
          style={leftControlButtonStyle}
        >
          <span aria-hidden="true" style={arrowDiscStyle}>
            <Icon name="caret-left" size="sm" />
          </span>
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll milestones right"
          onClick={handleScrollRight}
          style={rightControlButtonStyle}
        >
          <span aria-hidden="true" style={arrowDiscStyle}>
            <Icon name="caret-right" size="sm" />
          </span>
        </button>
      )}
    </div>
  );
}
